'use client';

import { useCallback, useEffect } from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { LuMessageCircle } from 'react-icons/lu';

import { useAppReadyOptional } from '@core/helpers/react/contexts/AppReadyContext';
import { Button } from '@/core/ui/primitives/button';
import { Badge } from '@/core/ui/primitives/badge';

export default function NotFound() {
  const appReady = useAppReadyOptional();
  // useSession can return undefined during SSG/prerendering when SessionProvider isn't available
  const session = useSession();
  const status = session?.status;
  const isAuthenticated = status === 'authenticated';

  useEffect(() => {
    // Some app shells (e.g., vault) gate initial render on a `content` signal.
    appReady?.signalReady('content');
  }, [appReady]);

  const handleContactSupportClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Only open feedback dialog for authenticated users (spam protection).
      // Keep the same markup for all auth states to avoid hydration mismatches.
      if (!isAuthenticated) return;
      event.preventDefault();
      window.dispatchEvent(new CustomEvent('feedback:open'));
    },
    [isAuthenticated],
  );

  return (
    <div className='mx-auto flex h-full min-h-[70vh] max-w-3xl flex-col items-center justify-center px-6 text-center'>
      <Badge variant='outline' className='text-xs font-semibold uppercase tracking-wide'>
        404
      </Badge>
      <h1 className='mt-6 text-3xl font-semibold text-foreground'>We can&apos;t find that page</h1>
      <p className='mt-4 max-w-2xl text-base text-muted-foreground'>
        The page you were looking for has moved or no longer exists. Double-check the address or go back.
      </p>
      <div className='mt-8'>
        <Button asChild size='lg'>
          <Link href='/contact' onClick={handleContactSupportClick}>
            <LuMessageCircle className='mr-2 h-4 w-4' />
            Contact support
          </Link>
        </Button>
      </div>
    </div>
  );
}
